'use client';

import React from 'react';

import { ApolloError } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import { GraphQLError } from 'graphql';
import { config } from '../../../config/bugsnag.config';
import { UNIVERSAL_ENV_VARS } from '../../constants/environmentVariables';
import { GetCurrentUser_currentUser } from '../../interfaces/graphql';
import { getMykissEnv } from '../environment/getMykissEnv';
import { isBrowser } from '../environment/isBrowser';
import { log, reportAPIError, reportApolloError, reportGraphQLError } from './errorUtils';

const releaseStage = getMykissEnv();
const bugsnagClient = Bugsnag.start({
  apiKey: UNIVERSAL_ENV_VARS.BUGSNAG_API_KEY,
  releaseStage,
  appVersion: UNIVERSAL_ENV_VARS.COMMIT_SHA,
  plugins: [new BugsnagPluginReact(React)],
  onError: function onError(report) {
    if (report.request.url) {
      report.request.decodedUrl = decodeURI(report.request.url);
    }
    // Output the error to console if running node in a dev environment.
    if (!config.stages.includes(releaseStage)) {
      log(report);
      return false;
    }

    if (!isBrowser()) {
      log(report);
    }

    return true;
  },
});

export function setErrorReportingUser(user?: GetCurrentUser_currentUser): void {
  bugsnagClient.setUser(
    user ? user._id : undefined,
    user?.privateFields.email ? user.privateFields.email : undefined,
    user ? user.nickname : undefined,
  );
}

const FailedErrorBoundary = (props: { children?: React.ReactNode }) => (
  <React.Fragment {...props} />
);

/*
 * This function is used to report errors that occur on the client.
 * Do not use this function to report errors in server components.
 * Use `reportMykissServerError` instead.
 */
export function reportMykissClientError(
  error?: Error | Response,
  context?: string,
  severity?: 'info' | 'warning',
  otherData?: object,
): void {
  // The below check of error.text is a replacement for `error instanceof Response`, as the Response
  // class is not available in the Node environment. A Fetch API response should have a `text`
  // function (we use it in `reportAPIError`), so we use it's existance to decide the error is from
  // a Fetch request.
  // @ts-ignore
  if (error?.text) {
    reportAPIError(bugsnagClient, error as Response, severity, otherData, context);
  } else if (error instanceof ApolloError) {
    reportApolloError(bugsnagClient, error, severity, otherData, context);
  } else if (error instanceof GraphQLError) {
    reportGraphQLError(bugsnagClient, error, severity, otherData, context);
  } else {
    bugsnagClient.notify(error as Error, event => {
      event.context = context;
      if ((error as Error).name === 'NotFoundError') {
        event.severity = severity ?? 'info';
      } else {
        event.severity = severity ?? 'error';
      }

      if (otherData) {
        event.addMetadata('other', otherData);
      }
      return true;
    });
  }
}

export function getReactErrorBoundary(): BugsnagErrorBoundary {
  const plugin = bugsnagClient.getPlugin('react');
  if (!plugin) {
    reportMykissClientError(
      new Error('Couldnt load ErrorBoundary as Bugsnag has no React plugin'),
      undefined,
      'warning',
    );
    return FailedErrorBoundary;
  }
  return plugin.createErrorBoundary();
}
